import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { EnvironmentService } from '@app/core/environment/environment.service';
import { ErrorService } from '@app/core/error/error.service';
import { Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { AbstractService } from './abstractService';
import { EntityServiceInterface } from '../interfaces/EntityServiceInterface';
import { EntityInterface } from '@app/shared/model/entity/interfaces/entityInterface.model';
import { KeyValue } from '@app/shared/model/keyValue.model';
import { CommentEntity } from '@app/shared/model/entity/commentEntity.model';
import { Criteria } from '@app/shared/model/entity/criteria.model';
import { Order } from '@app/shared/model/entity/order.model';

export abstract class AbstractEntityService extends AbstractService implements EntityServiceInterface {

  // ****************************** Propiedades **********************

  public responseCache = new Map();

  // ****************************** CONSTRUCTOR ***************************

  constructor(
    public httpClient: HttpClient,
    public environmentService: EnvironmentService,
    public router: Router,
    public errorService?: ErrorService,
    public isPatchUpdate: boolean = true
  ) {
    super(environmentService, router, errorService);
  }



  // ****************************** Metodos de acciones estandard *****

  getBaseBackUrl(): string {
    return this.baseBackUrl;
  }

  getBaseApiUrl(): string {
    return this.baseApiUrl;
  }

  public delete(id: string, comment: string, isWithComment: boolean, showError: boolean = true): Observable<void> {

    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/${id}`;
    if (isWithComment) {
      const entityComment = new CommentEntity();
      entityComment.comment = comment;
      return this.httpClient.post<void>(urlApi, entityComment, {headers: this.getHeaders()})
      // añadimos el manejo de errores
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.delete`) ));
    } else {
      return this.httpClient.delete<void>(urlApi, {headers: this.getHeaders()})
      // añadimos el manejo de errores
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.delete`) ));
    }
  }

  public findById(id: string, handleError: boolean = true, showError: boolean = true): Observable<EntityInterface> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/${id}`;
    return this.httpClient.get<EntityInterface>(urlApi, {headers: this.getHeaders()})
      // añadimos el manejo de errores
      .pipe(catchError((error: HttpErrorResponse) => {
        if (handleError) {
          return this.handleError(
            error,
            showError,
            `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.get`
          );
        } else {
          return throwError(error);
        }
      }));
  }

  public findByIdSimple(id: string, handleError: boolean = true, showError: boolean = true): Observable<EntityInterface> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/Simple/${id}`;
    return this.httpClient.get<EntityInterface>(urlApi, {headers: this.getHeaders()})
      // añadimos el manejo de errores
      .pipe(catchError((error: HttpErrorResponse) => {
        if (handleError) {
          return this.handleError(
            error,
            showError,
            `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.get`
          );
        } else {
          return throwError(error);
        }
      }));
  }

  public update(requestObjet: EntityInterface | CommentEntity, isWithComment: boolean, showError: boolean = true, isPath: boolean = this.isPatchUpdate): Observable<EntityInterface> {

    let entity: EntityInterface;
    if (isWithComment) {
      entity = (requestObjet as CommentEntity).entity;
    } else {
      entity = requestObjet;
    }

    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/${this.getURLIdFromEntity(entity)}`;
    let observable: Observable<EntityInterface>;
    if (isPath) {
      observable = this.httpClient.patch<EntityInterface>(urlApi, requestObjet, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.update`) ));
    } else {
      observable = this.httpClient.put<EntityInterface>(urlApi, requestObjet, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.update`) ));
    }
    return observable;
  }


  public endPreviousStartNew(requestObjet: EntityInterface | CommentEntity, showError: boolean = true): Observable<EntityInterface> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/endPreviousStartNew`;
    return this.httpClient.post<EntityInterface>(urlApi, requestObjet, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.insert`) ));
  }

  public create(requestObjet: EntityInterface | CommentEntity, showError: boolean = true): Observable<EntityInterface> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}`;
    return this.httpClient.post<EntityInterface>(urlApi, requestObjet, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.insert`) ));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public findAllByCriteria(filter: any, order?: Array<Order>, showError: boolean = true): Observable<EntityInterface[]> {
    const criteria: Criteria = new Criteria();
    criteria.filter = filter;
    criteria.order = order ? order : null;

    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/findAllByCriteria`;
    return this.httpClient.post<EntityInterface[]>(urlApi, criteria, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.getAll`) ));

  }

  public findAll(showError: boolean = true, specialist?: string, urlConjuncion: string = '/'): Observable<EntityInterface[]> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}${specialist ? urlConjuncion + specialist : ''}`;
    return this.httpClient.get<EntityInterface[]>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.getAll`) ));
  }

  public findSimpleByCode(code: string, showError: boolean = true, specialist?: string, cache = false): Observable<EntityInterface> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/findSimpleByCode${specialist ? '/' + specialist : ''}/${code}`;
    if (cache) {
      const cacheObject: EntityInterface = this.responseCache.get(urlApi) as EntityInterface;
      if (cacheObject) {
        return of(cacheObject);
      }
    }
    const response = this.httpClient.get<EntityInterface>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.getAll`) ));
    response.subscribe(cacheObject => this.responseCache.set(urlApi, cacheObject));
    return response;
  }

  public existsByCode(code: string, showError: boolean = true, specialist?: string, cache = false): Observable<boolean> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/existsByCode${specialist ? '/' + specialist : ''}/${code}`;
    if (cache) {
      const cacheObject: boolean = this.responseCache.get(urlApi) as boolean;
      if (cacheObject) {
        return of(cacheObject);
      }
    }
    const response = this.httpClient.get<boolean>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.getAll`) ));
      response.subscribe(cacheObject => this.responseCache.set(urlApi, cacheObject));
      return response;
  }

  public existsByListCodes(codes: Array<string>, showError: boolean = true, specialist?: string): Observable<Array<string>> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/existsListByCodes${specialist ? '/' + specialist : ''}`;
    return this.httpClient.post<Array<string>>(urlApi, codes, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, showError, `entity.${this.getEntityName().charAt(0).toLowerCase() + this.getEntityName().slice(1)}.error.insert`) ));
  }

  findKeyValueByKeyStartingWith(key: string, specialist?: string): Observable<Array<KeyValue>> {
    console.log('findKeyValueByKeyStartingWith');
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/findKeyValuesByKeyStartWith/${specialist ? specialist + '/' : ''}${key}`;
    return this.httpClient.get<KeyValue[]>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error) ));
  }
  findKeyValueByByValueContaining(value: string, specialist?: string): Observable<Array<KeyValue>> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/findKeyValuesByValueContains/${specialist ? specialist + '/' : ''}${value}`;
    return this.httpClient.get<KeyValue[]>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error) ));
  }
  findAllKeyValues(specialist?: string, cache = false): Observable<Array<KeyValue>> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/findAllKeyValues${specialist ? '/' + specialist : ''}`;
    if (cache) {
      const cacheObject: Array<KeyValue> = this.responseCache.get(urlApi) as Array<KeyValue>;
      if (cacheObject) {
        return of(cacheObject);
      }
    }
    const response = this.httpClient.get<Array<KeyValue>>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error) ));
    response.subscribe(cacheObject => this.responseCache.set(urlApi, cacheObject));
    return response;
  }

  findKeyValueByKey(key: string, specialist?: string): Observable<KeyValue> {
    const urlApi = `${this.baseBackUrl}${this.baseApiUrl}/${this.getEntityName()}/findKeyValueByKey/${specialist ? specialist + '/' : ''}${key}`;
    return this.httpClient.get<KeyValue>(urlApi, {headers: this.getHeaders()})
        // añadimos el manejo de errores
        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error) ));
  }


  // ****************************** Abstractor *************************

  abstract getEntityName(): string;
  // abstract getIdFromEntity(entity: EntityInterface): any;
  abstract getURLIdFromEntity(entity: EntityInterface): string;

}
